import { ArrowLeft, BadgeCheck, CheckCircle2, Clock3, FileText, GraduationCap, Search, Sparkles, Target } from 'lucide-react'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'
import { recordStudentGrowthEvent } from '../api.js'
import {
  STUDENT_CERTIFICATES,
  STUDENT_CERTIFICATE_FILTERS,
  certificateMatchesFilter,
  certificateScore,
} from '../student-certificates.js'
import { getLearningResourcePath } from '../student-learning-resources.js'

const STUDENT_PROFILE_KEY = 'student_profile'
const CERTIFICATE_PLAN_KEY = 'student_plan_certificates'

const PROFILE_OPTIONS = {
  majorGroup: [
    ['computer', '计算机 / 软件 / AI'],
    ['ecommerce', '电商 / 运营 / 新媒体'],
    ['design', '设计 / 数字媒体'],
    ['business', '经管 / 财会 / 金融'],
    ['language', '外语 / 文科'],
    ['education', '教育 / 师范'],
    ['engineering', '电子 / 自动化 / 工程'],
    ['medical', '医学 / 护理 / 健康'],
    ['general', '暂时不确定'],
  ],
  grade: [
    ['freshman', '大一'],
    ['sophomore', '大二'],
    ['junior', '大三'],
    ['senior', '大四'],
    ['graduate', '毕业/求职中'],
  ],
  goal: [
    ['internship', '找实习'],
    ['resume', '补简历'],
    ['competition', '比赛/项目'],
    ['portfolio', '作品集'],
    ['exam', '考试升学'],
    ['general', '先打基础'],
  ],
}

function pickText(value) {
  return value?.zh || value?.en || ''
}

function pickList(value) {
  return Array.isArray(value?.zh) ? value.zh : []
}

function readJson(key, fallback) {
  if (typeof window === 'undefined') return fallback
  try {
    const parsed = JSON.parse(window.localStorage.getItem(key) || 'null')
    return parsed || fallback
  } catch {
    return fallback
  }
}

function writeJson(key, value) {
  if (typeof window === 'undefined') return
  window.localStorage.setItem(key, JSON.stringify(value))
}

function optionLabel(type, key) {
  return PROFILE_OPTIONS[type].find(([value]) => value === key)?.[1] || key
}

function worthLabel(item) {
  if (['cet-46', 'ruankao', 'huawei-hcia', 'teacher-cert', 'junior-accountant', 'nurse'].includes(item.id)) {
    return '值得优先规划'
  }
  if (['driver-license', 'office-wps', 'new-media-cert', 'adobe', 'health-manager', 'nutrition'].includes(item.id)) {
    return '看目标岗位再决定'
  }
  return item.categoryKey === 'computer' || item.categoryKey === 'language'
    ? '有明确方向时值得'
    : '不要盲目堆证'
}

function recommendationIndex(item, profile) {
  return Math.min(5, Math.max(2, Math.round(certificateScore(item, profile) / 12)))
}

function todayTask(item) {
  return `今天先确认「${pickText(item.title)}」是否匹配你的目标岗位，再整理一页备考笔记。`
}

function CertificateCard({ added, item, onAdd, profile }) {
  const tags = pickList(item.tags)
  const cautions = pickList(item.cautions)
  const index = recommendationIndex(item, profile)
  return (
    <article className="certificate-plan-card">
      <header>
        <span><BadgeCheck size={16} /> {pickText(item.category)}</span>
        <h2>{pickText(item.title)}</h2>
        <strong>推荐指数：{index} / 5</strong>
      </header>
      <p>{pickText(item.value)}</p>
      <div className="certificate-tag-row">
        {tags.slice(0, 4).map((tag) => <span key={tag}>{tag}</span>)}
        <span>{worthLabel(item)}</span>
      </div>
      <dl className="certificate-plan-meta">
        <div><dt>适合人群</dt><dd>{item.majorGroups.map((key) => optionLabel('majorGroup', key)).join(' / ')}</dd></div>
        <div><dt>适合方向</dt><dd>{item.suitableGoals.map((key) => optionLabel('goal', key)).join(' / ')}</dd></div>
        <div><dt>准备周期</dt><dd>{pickText(item.preparationCycle)}</dd></div>
        <div><dt>是否值得</dt><dd>{worthLabel(item)}</dd></div>
      </dl>
      <section className="certificate-advice">
        <strong><FileText size={15} /> 对简历帮助</strong>
        <p>{pickText(item.resumeUsage)}</p>
      </section>
      <section className="certificate-advice">
        <strong><Clock3 size={15} /> 备考建议</strong>
        <p>{pickText(item.whenToPrepare)}</p>
      </section>
      <section className="certificate-advice is-warning">
        <strong>避坑提醒</strong>
        <ul>{cautions.map((item) => <li key={item}>{item}</li>)}</ul>
      </section>
      <section className="certificate-advice">
        <strong><Target size={15} /> 今日备考任务</strong>
        <p>{todayTask(item)}</p>
      </section>
      <button className={added ? 'secondary-button' : 'primary-button'} type="button" onClick={() => onAdd(item)}>
        <CheckCircle2 size={16} />
        {added ? '已加入成长计划' : '加入成长计划'}
      </button>
      <Link className="secondary-button" to={getLearningResourcePath(pickText(item.title))}>
        <FileText size={16} />
        学习资源
      </Link>
    </article>
  )
}

function StudentCertificatesPage() {
  const navigate = useNavigate()
  const storedProfile = readJson(STUDENT_PROFILE_KEY, null)
  const [profile, setProfile] = useState(() => storedProfile || { majorGroup: 'general', grade: 'junior', goal: 'resume' })
  const [filter, setFilter] = useState('all')
  const [query, setQuery] = useState('')
  const [plan, setPlan] = useState(() => readJson(CERTIFICATE_PLAN_KEY, []))
  const [notice, setNotice] = useState('')

  const certificates = useMemo(() => (
    STUDENT_CERTIFICATES
      .filter((item) => certificateMatchesFilter(item, filter))
      .filter((item) => {
        const needle = query.trim()
        if (!needle) return true
        return `${pickText(item.title)} ${pickText(item.category)} ${pickText(item.value)}`.includes(needle)
      })
      .sort((left, right) => certificateScore(right, profile) - certificateScore(left, profile))
  ), [filter, profile, query])

  function updateProfile(key, value) {
    const next = { ...profile, [key]: value, updatedAt: new Date().toISOString() }
    setProfile(next)
    writeJson(STUDENT_PROFILE_KEY, next)
  }

  async function addCertificate(item) {
    const exists = plan.some((entry) => entry.id === item.id)
    const next = exists
      ? plan
      : [...plan, { id: item.id, title: pickText(item.title), task: todayTask(item), addedAt: new Date().toISOString() }]
    setPlan(next)
    writeJson(CERTIFICATE_PLAN_KEY, next)
    setNotice(exists ? '这个证书已经在你的成长计划里。' : `已加入成长计划：${pickText(item.title)}`)
    try {
      await recordStudentGrowthEvent({
        eventType: 'certificate_added',
        title: `加入证书规划：${pickText(item.title)}`,
        description: todayTask(item),
        metadata: { certificateId: item.id, profile },
      })
    } catch {
      // Guest users can still keep a local plan.
    }
  }

  return (
    <main className="page-container certificate-plan-page">
      <div className="student-project-rec-topbar">
        <button type="button" className="secondary-button" onClick={() => navigate('/growth')}>
          <ArrowLeft size={16} /> 返回学生成长
        </button>
        <button type="button" className="secondary-button" onClick={() => navigate('/growth')}>
          <ArrowLeft size={16} /> 返回成长中心
        </button>
      </div>
      <section className="certificate-plan-hero">
        <span><GraduationCap size={16} /> 证书规划</span>
        <h1>哪些证值得考，哪些别浪费时间？先按目标筛一遍。</h1>
        <p>证书不是越多越好。这里会告诉你推荐指数、是否值得考、简历帮助和今天能做的一步。</p>
      </section>

      <section className="certificate-plan-controls">
        {Object.entries(PROFILE_OPTIONS).map(([key, options]) => (
          <label key={key}>
            <span>{key === 'majorGroup' ? '专业方向' : key === 'grade' ? '年级' : '当前目标'}</span>
            <select value={profile[key]} onChange={(event) => updateProfile(key, event.target.value)}>
              {options.map(([value, label]) => <option key={value} value={value}>{label}</option>)}
            </select>
          </label>
        ))}
        <label>
          <span>搜索证书</span>
          <div>
            <Search size={16} />
            <input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="四级、软考、HCIA、教资..." />
          </div>
        </label>
      </section>

      <div className="student-competition-filters" aria-label="证书分类">
        {STUDENT_CERTIFICATE_FILTERS.map((item) => (
          <button
            aria-pressed={filter === item.key}
            className={filter === item.key ? 'is-active' : ''}
            key={item.key}
            type="button"
            onClick={() => setFilter(item.key)}
          >
            {item.zh}
          </button>
        ))}
      </div>

      {notice ? <p className="student-growth-error is-success">{notice}</p> : null}

      <section className="certificate-plan-nudge">
        <Sparkles size={18} />
        <div>
          <strong>建议只选 1-2 个主证书</strong>
          <p>如果证书不能服务目标岗位、项目作品或实习投递，就先把时间留给作品和简历。</p>
        </div>
      </section>

      <section className="certificate-plan-grid">
        {certificates.map((item) => (
          <CertificateCard
            added={plan.some((entry) => entry.id === item.id)}
            item={item}
            key={item.id}
            onAdd={addCertificate}
            profile={profile}
          />
        ))}
      </section>
    </main>
  )
}

export default StudentCertificatesPage
